import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe } from "lucide-react";
import { applyDocumentDirection } from "@/lib/direction";

interface LanguageSelectorProps {
  variant?: "light" | "dark";
}

export const LanguageSelector = ({ variant = "light" }: LanguageSelectorProps) => {
  const { i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    applyDocumentDirection(lng);
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const activeLanguageCode = i18n.resolvedLanguage?.split('-')[0] ?? i18n.language?.split('-')[0] ?? 'en';
  const currentLanguage = languages.find(lang => lang.code === activeLanguageCode) || languages[0];

  // Dark variant styles (for dark backgrounds)
  const darkButtonStyles = "border-primary/20 bg-primary/10 hover:bg-primary/15 hover:border-primary/40 text-primary";
  const darkDropdownStyles = "bg-card/95 backdrop-blur-xl border-border text-foreground";
  const darkItemStyles = "hover:bg-primary/10";
  const darkActiveStyles = "bg-primary/20 text-primary";
  const darkInactiveStyles = "text-foreground";

  // Light variant styles (for light backgrounds)
  const lightButtonStyles = "border-border bg-background hover:bg-accent hover:text-accent-foreground";
  const lightDropdownStyles = "bg-popover backdrop-blur-xl border-border";
  const lightItemStyles = "hover:bg-accent hover:text-accent-foreground";
  const lightActiveStyles = "bg-primary/10 text-primary";
  const lightInactiveStyles = "";

  const isDark = variant === "dark";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`gap-2 transition-all ${isDark ? darkButtonStyles : lightButtonStyles}`}
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
          <span className="sm:hidden">{currentLanguage.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={isDark ? darkDropdownStyles : lightDropdownStyles}>
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            className={`cursor-pointer transition-colors ${isDark ? darkItemStyles : lightItemStyles} ${
              i18n.language === language.code 
                ? (isDark ? darkActiveStyles : lightActiveStyles)
                : (isDark ? darkInactiveStyles : lightInactiveStyles)
            }`}
          >
            <span className="me-2">{language.flag}</span>
            {language.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
